/**
 * Pengiriman form tanpa memuat ulang halaman.
 *
 * Seluruh <form> di aplikasi dikirim lewat fetch, bukan submit bawaan browser.
 * Controller tidak perlu tahu: ia tetap membalas seperti submit biasa (redirect
 * + pesan flash, atau redirect balik dengan error validasi dan old input). HTML
 * hasil akhirnya menggantikan isi <body>, lalu Alpine menginisialisasi komponen
 * yang baru masuk.
 *
 * Form yang sudah ditangani sendiri oleh Alpine (@submit.prevent + fetch JSON)
 * dilewati karena event submit-nya sudah dicegah.
 */

const PROGRESS_ATTRIBUTE = 'data-page-visiting';

let activeVisit = null;
let renderedUrl = window.location.href;

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

function withoutHash(url) {
    const parsed = new URL(url, window.location.href);
    parsed.hash = '';

    return parsed.toString();
}

function pathOf(url) {
    return new URL(url, window.location.href).pathname;
}

/**
 * Halaman hasil hanya boleh ditempel ke dokumen yang memuat aset yang sama;
 * setelah deploy baru, nama aset berbeda dan halaman harus dibuka penuh.
 */
function assetSignature(doc) {
    return [...doc.querySelectorAll('head script[src], head link[rel="stylesheet"]')]
        .map((element) => element.getAttribute('src') ?? element.getAttribute('href'))
        .sort()
        .join('|');
}

function syncHead(doc) {
    document.title = doc.title;

    for (const name of ['csrf-token', 'robots']) {
        const incoming = doc.head.querySelector(`meta[name="${name}"]`);
        const current = document.head.querySelector(`meta[name="${name}"]`);

        if (incoming && current) {
            current.setAttribute('content', incoming.getAttribute('content') ?? '');
        } else if (incoming) {
            document.head.appendChild(incoming.cloneNode());
        } else if (current) {
            current.remove();
        }
    }
}

function swapBody(doc) {
    const scripts = [...doc.body.querySelectorAll('script')];
    scripts.forEach((script) => script.remove());

    // Fungsi x-data halaman (mis. posApp) harus sudah terdefinisi sebelum Alpine
    // menginisialisasi elemen baru, jadi script dijalankan lebih dulu.
    for (const source of scripts) {
        const script = document.createElement('script');

        for (const { name, value } of source.attributes) {
            script.setAttribute(name, value);
        }

        script.textContent = source.textContent;
        document.head.appendChild(script);
        script.remove();
    }

    for (const { name } of [...document.body.attributes]) {
        document.body.removeAttribute(name);
    }

    for (const { name, value } of doc.body.attributes) {
        document.body.setAttribute(name, value);
    }

    // Alpine mengamati perubahan DOM: komponen lama dibersihkan (termasuk
    // destroy()) dan komponen baru diinisialisasi otomatis.
    document.body.replaceChildren(...doc.body.childNodes);
}

function updateHistory(url, mode) {
    if (mode === 'none') {
        return;
    }

    if (mode === 'push' && withoutHash(url) !== withoutHash(window.location.href)) {
        window.history.pushState({ pageVisit: true }, '', url);
    } else {
        window.history.replaceState({ pageVisit: true }, '', url);
    }
}

/**
 * Halaman error (403, 404, 419, 500) punya gaya sendiri tanpa aset aplikasi,
 * jadi seluruh dokumen diganti apa adanya.
 */
function replaceDocument(html, url, historyMode) {
    updateHistory(url, historyMode);
    document.open();
    document.write(html);
    document.close();
}

function lockForm(form) {
    form.setAttribute('aria-busy', 'true');

    for (const button of form.querySelectorAll('button:not([type]), button[type="submit"], input[type="submit"]')) {
        if (! button.disabled) {
            button.disabled = true;
            button.setAttribute('data-visit-locked', '');
        }
    }
}

function unlockForm(form) {
    form.removeAttribute('aria-busy');

    for (const button of form.querySelectorAll('[data-visit-locked]')) {
        button.disabled = false;
        button.removeAttribute('data-visit-locked');
    }
}

function showConnectionError() {
    const toast = document.createElement('div');
    toast.setAttribute('role', 'alert');
    toast.className = 'page-visit-toast';
    toast.textContent = 'Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
}

/**
 * Membuka URL dan menempelkan hasilnya tanpa memuat ulang halaman.
 *
 * @param {string} url
 * @param {{
 *     method?: string,
 *     body?: FormData|null,
 *     history?: 'push'|'replace'|'none',
 *     preserveScroll?: boolean|null,
 *     background?: boolean,
 *     form?: HTMLFormElement|null,
 * }} options  background dipakai pembaruan berkala: tidak menampilkan penanda
 *              proses dan tidak menyela kiriman form yang sedang berjalan.
 */
export async function visit(url, options = {}) {
    const {
        method = 'GET',
        body = null,
        history = 'push',
        preserveScroll = null,
        background = false,
        form = null,
    } = options;

    if (background && activeVisit) {
        return;
    }

    activeVisit?.abort();
    const controller = new AbortController();
    activeVisit = controller;

    if (! background) {
        document.documentElement.setAttribute(PROGRESS_ATTRIBUTE, '');
    }

    try {
        const response = await fetch(url, {
            method,
            body,
            credentials: 'same-origin',
            headers: {
                Accept: 'text/html, application/xhtml+xml',
                'X-CSRF-TOKEN': csrfToken(),
            },
            signal: controller.signal,
        });

        const finalUrl = response.url || url;
        const html = await response.text();

        if (! (response.headers.get('Content-Type') ?? '').includes('text/html')) {
            window.location.assign(finalUrl);

            return;
        }

        if (! response.ok) {
            replaceDocument(html, finalUrl, history);

            return;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');

        if (assetSignature(doc) !== assetSignature(document)) {
            window.location.assign(finalUrl);

            return;
        }

        const keepScroll = preserveScroll ?? pathOf(finalUrl) === pathOf(window.location.href);
        const scroll = { left: window.scrollX, top: window.scrollY };

        updateHistory(finalUrl, history);
        syncHead(doc);
        swapBody(doc);
        renderedUrl = finalUrl;

        requestAnimationFrame(() => {
            window.scrollTo(keepScroll ? scroll : { left: 0, top: 0 });
            document.body.querySelector('[autofocus]')?.focus();
        });
    } catch (error) {
        if (error.name === 'AbortError') {
            return;
        }

        if (form) {
            unlockForm(form);
        }

        if (! background) {
            showConnectionError();
        }
    } finally {
        if (activeVisit === controller) {
            activeVisit = null;
            document.documentElement.removeAttribute(PROGRESS_ATTRIBUTE);
        }
    }
}

function handleSubmit(event) {
    const form = event.target;

    if (event.defaultPrevented || ! (form instanceof HTMLFormElement)) {
        return;
    }

    const submitter = event.submitter ?? null;
    const target = submitter?.getAttribute('formtarget') ?? form.getAttribute('target');
    const method = (submitter?.getAttribute('formmethod') ?? form.getAttribute('method') ?? 'GET').toUpperCase();
    const action = new URL(
        submitter?.getAttribute('formaction') ?? form.getAttribute('action') ?? window.location.href,
        window.location.href,
    );

    if ((target && target !== '_self') || method === 'DIALOG' || action.origin !== window.location.origin) {
        return;
    }

    event.preventDefault();

    const formData = submitter ? new FormData(form, submitter) : new FormData(form);
    lockForm(form);

    if (method === 'GET') {
        const entries = [...formData].filter(([, value]) => typeof value === 'string');
        action.search = new URLSearchParams(entries).toString();
        visit(action.toString(), { form });

        return;
    }

    visit(action.toString(), { method: 'POST', body: formData, form });
}

export function startPageVisits() {
    window.visit = visit;

    document.addEventListener('submit', handleSubmit);

    window.addEventListener('popstate', () => {
        // Perpindahan anchor (#bagian) tidak perlu mengambil ulang halaman.
        if (withoutHash(window.location.href) === withoutHash(renderedUrl)) {
            return;
        }

        visit(window.location.href, { history: 'none', preserveScroll: false });
    });
}
